/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, SyntheticEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Plx from 'react-plx';

import { Grid, Typography } from '@mui/material';

import { Namespaces } from '../../../../i18n';
import { StatisticsMap } from '../StatisticsMap/StatisticsMap';
import { Countries } from './StatisticsSection.consts';
import { createStatisticsSectionStyles } from './StatisticsSection.styles';

const parallaxData = [
  {
    start: '.plxStatistics',
    duration: '40vh',
    properties: [
      {
        startValue: -40,
        endValue: 0,
        unit: 'vh',
        property: 'translateY',
      },
      {
        startValue: 0,
        endValue: 1,
        property: 'opacity',
      },
    ],
  },
];

export const StatisticsSection = memo(() => {
  const classes = createStatisticsSectionStyles();
  const { t } = useTranslation(Namespaces.About);
  const [selectedCountry, setSelectedCountry] = useState<Countries | string>('');

  const onHoverHandler = (e: SyntheticEvent) => {
    setSelectedCountry(e.currentTarget.id);
  };

  return (
    <Grid container className={classes.root}>
      <Plx className={cx('plxStatistics', classes.plxStatistics)} parallaxData={parallaxData}>
        <div className={classes.container}>
          <Typography variant="h1" classes={{ h1: classes.title }}>
            {t('statistics.title')}
          </Typography>
          <Typography variant="h4" classes={{ h4: classes.description }}>
            <Trans>{t('statistics.description')}</Trans>
          </Typography>
        </div>
        <div className={classes.mapWrapper}>
          <StatisticsMap selectedCountry={selectedCountry} />
          <Grid
            container
            classes={{
              root: cx(classes.container, classes.statisticsContainer),
            }}
          >
            <Grid item xs={3} classes={{ root: classes.statisticsItem }}>
              <Typography variant="h1" classes={{ root: classes.statisticsItemTitle }}>
                5
              </Typography>
              <Typography variant="body1" classes={{ root: classes.statisticsItemDescription }}>
                {t('statistics.offices')}
              </Typography>
            </Grid>

            <Grid item xs={3} classes={{ root: classes.statisticsItem }}>
              <Typography variant="h1" classes={{ root: classes.statisticsItemTitle }}>
                300+
              </Typography>
              <Typography variant="body1" classes={{ root: classes.statisticsItemDescription }}>
                {t('statistics.clients')}
              </Typography>
            </Grid>

            <Grid item xs={3} classes={{ root: classes.statisticsItem }}>
              <Typography variant="h1" classes={{ root: classes.statisticsItemTitle }}>
                500+
              </Typography>
              <Typography variant="body1" classes={{ root: classes.statisticsItemDescription }}>
                {t('statistics.projects')}
              </Typography>
            </Grid>

            <Grid item xs={3} classes={{ root: classes.statisticsItem }}>
              <Typography variant="h1" classes={{ root: classes.statisticsItemTitle }}>
                {t('statistics.million', { count: 1 })}
              </Typography>
              <Typography variant="body1" classes={{ root: classes.statisticsItemDescription }}>
                {t('statistics.lines_of_code')}
              </Typography>
            </Grid>
          </Grid>
          <Grid container classes={{ root: classes.mapLegend }}>
            <Grid item md={12}>
              <Typography variant="body1" classes={{ root: classes.mapLegendText }}>
                {t('statistics.legend.our_headquarter_is_located')}&nbsp;
                <i onMouseEnter={onHoverHandler} onMouseLeave={() => setSelectedCountry('')} id={Countries.austria}>
                  <Trans>{t('statistics.legend.wollersdorf_austria')}</Trans>
                </i>
                <span>
                  ,&nbsp;{t('statistics.legend.with_further_locations_in')}
                  &nbsp;
                </span>
                <i onMouseOver={onHoverHandler} onMouseLeave={() => setSelectedCountry('')} id={Countries.estonia}>
                  {t('statistics.legend.estonia')}
                </i>
                ,&nbsp;
                <i onMouseOver={onHoverHandler} onMouseLeave={() => setSelectedCountry('')} id={Countries.switzerland}>
                  {t('statistics.legend.switzerland')}
                </i>
                ,&nbsp;
                <i onMouseOver={onHoverHandler} onMouseLeave={() => setSelectedCountry('')} id={Countries.south_africa}>
                  {t('statistics.legend.south_africa')}
                </i>
                ,<br />
                {t('statistics.legend.and')}&nbsp;
                <i onMouseOver={onHoverHandler} onMouseLeave={() => setSelectedCountry('')} id={Countries.uruguay}>
                  {t('statistics.legend.uruguay')}
                </i>
                .
              </Typography>
            </Grid>
          </Grid>
        </div>
      </Plx>
    </Grid>
  );
});
